import moment from 'moment'
import { callAuthorizedGraphQLEndpoint } from '../../Module/graphQL'
import { normalizeGraphQLConnection } from '../../shared/graphql/utils'
import { convertArrayToHashMap } from '../../shared/libraries/dataSets'
import { prepareCampaignsData /*isDeclinedCampaign*/ } from './utils'

/**
 * Fetches clients by chunks while there is a next page using recursion
 * @param {number} params.chunkSize Size of the request
 * @param {number} params.skip Amount of items to skip
 */
export const fetchAllClientsByChunks = async ({
  chunkSize = 100,
  skip = 0,
}) => {
  const gql = `
    query ($first: Int, $skip: Int) {
      clients (first:$first, skip:$skip) {
        pageInfo{
          hasNextPage
        }
        edges {
          node {
            id
            name
            countryId
          }
        }
      }
      countries {
        nodes {
          id
          name
        }
      }
    }
  `
  const res = await callAuthorizedGraphQLEndpoint(gql, {
    first: chunkSize,
    skip,
  })

  const { data } = await res.json()

  let countriesResult = data.countries.nodes
  let clientsResult = data.clients.edges.map(edge => edge.node)

  // If there's a next page fetch it with skip
  if (data.clients.pageInfo.hasNextPage) {
    const { clients, countries } = await fetchAllClientsByChunks({
      chunkSize,
      skip: chunkSize + skip,
    })
    countriesResult = [...countriesResult, ...countries]
    clientsResult = [...clientsResult, ...clients]
  }
  const uniqueCountries = Object.values(
    convertArrayToHashMap(countriesResult, 'id'),
  )

  return {
    countries: uniqueCountries,
    clients: clientsResult,
  }
}

/**
 * Fetch campaigns data
 */
export const fetchCampaignsData = async ({
  isClient,
  shouldFetchActiveDates,
  variables,
  signal,
}) => {
  const gql = `
    query (
      $clientIds: [ID!],
      $countryIds: [ID!],
      $types: [CampaignTypeEnum!],
      $subtypes: [CampaignSubtypeEnum!],
      $statuses: [CampaignStatusEnum!],
      $startDate: String,
      $endDate: String,
      $first: Int,
      $skip: Int,
      $isSelfServe: Boolean,
      $orderBy: CampaignOrderByEnum,
    ) {
      campaigns(
        first: $first, 
        skip: $skip,
        clientIds: $clientIds, 
        countryIds: $countryIds, 
        types: $types,
        subtypes: $subtypes,
        statuses: $statuses,
        startDate: $startDate,
        endDate: $endDate,
        isSelfServe: $isSelfServe,
        orderBy: $orderBy,
      ){
        totalCount
        pageInfo {
          hasNextPage
        }
        edges {
          node {
            id
            name
            type
            subtype
            status
            createdAt
            isSelfServe
            scanDataEnabled
            startDate
            offerExpirationDate
            noExpiration
            lastConversionDate
            lastMissionDate
            reviewMeetingDate
            workflowId
            country {
              name
              currencySign
            }
            client {
              name
              logo
            }
            cityPlans {
              totalCount
            }
            selfServeData {
              lastActiveStep
              internalStatus
              startDate
              paymentStatus
            }
            isSelfServe
            ${
              shouldFetchActiveDates
                ? `
                  activeDates{
                    start
                    end
                  }`
                : ''
            }
          }
        }
      }
    }
  `
  const res = await callAuthorizedGraphQLEndpoint(
    gql,
    variables,
    {},
    { signal },
  )

  const result = await res.json()

  if (result.errors && result.errors.length)
    throw new Error(result.errors[0].message)

  const {
    data: {
      campaigns: {
        edges: campaignEdges,
        pageInfo: { hasNextPage },
      },
    },
  } = result

  const data = campaignEdges.map(({ node }) => {
    let campaignTimeRange = null
    let campaignDataForClient = {}

    // TODO: REMOVE THIS LOGIC AFTER ADDING INTERNAL STATUS REJECTED
    // const isDeclined = node.selfServeData.length
    //   ? isDeclinedCampaign({
    //       internalStatus: node.selfServeData[0].internalStatus,
    //       paymentStatus: node.selfServeData[0].paymentStatus,
    //     })
    //   : null
    // if (isDeclined) {
    //   node.selfServeData[0].internalStatus = 'REJECTED'
    // }

    if (isClient) {
      const {
        data: {
          inventoryItems: { edges },
        },
      } = result
      const campaignFlyers = edges.filter(
        flyer => flyer.node.campaignId === node.id,
      )
      campaignDataForClient = prepareCampaignsData(node, campaignFlyers)
    }
    const formatDate = date =>
      date ? new Date(date).toLocaleDateString() : null

    if (shouldFetchActiveDates) {
      // THIS DATES ARE FROM CampaignActiveRange and they are the first and last missions dates.
      const startDate = node.activeDates.start
      const endDate = node.activeDates.end

      if (startDate && endDate) {
        const startDateFormatted = formatDate(startDate)
        const endDateFormatted = formatDate(endDate)
        campaignTimeRange = `${startDateFormatted} - ${endDateFormatted}`
      }
    }

    const formatDateUTC = date =>
      date
        ? moment(
            moment(Number(date))
              .utc()
              .format()
              .split('T')[0],
            'YYYY-MM-DD',
            true,
          ).format('YYYY-MM-DD')
        : null

    return {
      ...node,
      ...campaignDataForClient,
      id: node.id,
      name: node.name,
      type: node.type,
      country: node.country.name,
      currencySign: node.country.currencySign,
      status: node.status,
      client: node.client.name,
      clientLogo: node.client.logo,
      totalCities: node.cityPlans.totalCount,
      campaignTimeRange,
      startDate: formatDateUTC(node.startDate),
      endDate: node.offerExpirationDate
        ? formatDateUTC(node.offerExpirationDate)
        : formatDateUTC(node.lastMissionDate),
      lastConversionDate: formatDate(Number(node.lastConversionDate)),
      scans:
        isClient && node.scanUsersData.length ? node.scanUsersData.length : 0,
    }
  })

  const sortedCampaignsByClient = data
    .filter(campaign => !campaign.workflowId)
    .reduce((acc, item) => {
      acc[item.client] = [...(acc[item.client] || []), item].sort((a, b) => {
        if (a.startDate < b.startDate) return -1
        if (a.startDate > b.startDate) return 1
        return 0
      })
      return Object.values(acc).flat()
    }, [])

  return {
    data: sortedCampaignsByClient,
    hasNextPage,
    totalCount: result.data.campaigns.totalCount,
  }
}

export const fetchCountOfCalculatedCampaigns = async () => {
  const gql = `
    query {
      countOfCalculatedCampaigns
    }
  `

  const res = await callAuthorizedGraphQLEndpoint(gql)

  const result = await res.json()

  if (result.errors && result.errors.length)
    throw new Error(result.errors[0].message)

  const {
    data: { countOfCalculatedCampaigns },
  } = result

  return !!countOfCalculatedCampaigns
}

export const fetchCampaignsDashboardData = async (initialVariables, signal) => {
  const variables = {
    ...initialVariables,
  }

  const gql = `
  query (
    $clientIds: [ID!],
    $countryIds: [ID!],
    $types: [CampaignTypeEnum!],
    $subtypes: [CampaignSubtypeEnum!],
    $statuses: [CampaignStatusEnum!],
    $startDate:String,
    $endDate:String,
    $first: Int,
    $skip: Int,
    $campaignIds: [ID]
  ) {
    campaigns(
      first: $first, 
      skip: $skip,
      clientIds:$clientIds, 
      countryIds:$countryIds, 
      types: $types,
      subtypes: $subtypes,
      statuses: $statuses,
      startDate: $startDate,
      endDate: $endDate,
      campaignIds: $campaignIds
    ){
      totalCount
      pageInfo {
        hasNextPage
      }
      edges {
        node {
          workflowId
          dashboardsData {
            id,
            name,
            status,
            type,
            totalCities,
            startDate,
            endDate,
            flyersDistributed,
            scans,
            scansCVR,
            mailPieces,
            totalMissions,
            progressScore,
            costs {
              printingCostForMission,
              permitCostForMission,
              bonusCostForMission,
              distributionCost,
              hardCosts
            },
            metrics {
              id,
              name,
              label,
              isPrimary,
              metricValue,
              conversionRate,
              costPerMetric
            }
            missions {
              id,
              status,
              type,
              totalFlyersDistributed,
              printingCostPerFlyer,
              distributionCost,
              bonusPerConversion,
              bonusRedemptionPeriod,
              dateTime,
              packSize,
              cityPlanId,
              metrics {
                name,
                metricId,
                isPrimary,
                conversionsCount,
                conversionsWithBonusCount
              },
              hardCosts,
              amountScans
            }
          }
        }
      }
    }
  }
`

  const res = await callAuthorizedGraphQLEndpoint(
    gql,
    variables,
    {},
    { signal },
  )

  const result = await res.json()

  if (result.errors && result.errors.length)
    throw new Error(result.errors[0].message)

  const {
    data: { campaigns },
  } = result

  let data = normalizeGraphQLConnection(campaigns)
    .filter(campaign => !campaign.workflowId)
    .map(({ dashboardsData }) => prepareCampaignsData(dashboardsData))

  return {
    totalCount: campaigns.totalCount,
    hasNextPage: campaigns.pageInfo.hasNextPage,
    data,
  }
}

/**
 * Fetch list of client campaigns
 */
export const fetchClientCampaignsList = async variables => {
  const gql = `
    query (
      $clientIds: [ID!],
      $first: Int,
      $skip: Int,
    ) {
      campaigns(
        first: $first, 
        skip: $skip,
        clientIds:$clientIds, 
      ){
        edges {
          node {
            id
            name
            type
            status
            subtype
          }
        }
      }
    }
  `
  const res = await callAuthorizedGraphQLEndpoint(gql, variables)

  const result = await res.json()

  return normalizeGraphQLConnection(result.data.campaigns)
}

// const mlAPIRoot = 'https://ml-api.oppizi.com'
// export const createAIqTable = async ({
//   campaignId,
//   clientId,
//   cityId,
//   cityName,
//   campaignMonthDuration,
//   primaryMetricName,
// }) => {
//   const req = await fetch(`${mlAPIRoot}/qtable/create_q_table`, {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/json',
//       'x-api-key': `8euHDMFhnha1wFjfExEqp7iPEAOfVHfX42Xzu8J3`,
//     },
//     body: JSON.stringify({
//       'city_id': cityId,
//       'city_name': cityName,
//       'campaign_id': campaignId,
//       'client_id': clientId,
//       'primary_metric': primaryMetricName,
//       'month_duration': campaignMonthDuration,
//     }),
//   })
//   return req.json()
// }

// export const generateMissions = async ({ token, encodedParams }) => {
//   const response = await fetch(
//     `https://api-nostradamus.oppizi.com/v1/generate_missions?${encodedParams}`,
//     {
//       method: 'GET',
//       headers: {
//         'Content-Type': 'application/json',
//         'Authorization': `Bearer ${token}`,
//       },
//     },
//   )
//   const result = await response.json()
//   return result
// }

export const generateFootFallData = async ({ token, encodedParams }) => {
  const response = await fetch(
    `https://api-nostradamus.oppizi.com/v1/generate_heatmap?${encodedParams}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    },
  )
  const result = await response.json()
  return result
}
